import { useCallback } from 'react'

import { getUsedSpaceForecast } from '@/features/domain/order'
import { useNotification } from '@/hooks'
import { useAppDispatch } from '@/store'

import { updatePlanningRange } from '@/features/domain/territory'
import { useTexts } from '../useTexts'
import { useController } from './useController'
import { unwrapResult } from '@reduxjs/toolkit'

export function useControllerActions() {
  const toast = useNotification()
  const texts = useTexts()
  const dispatch = useAppDispatch()

  const {
    update,
    updateData,
    close,
    data: { newPlanningRange, format },
  } = useController()

  const onPlanningRangeChange = useCallback(
    async (newPlanningRange: number) => {
      try {
        update({ status: 'submitting' })

        const result = await dispatch(
          getUsedSpaceForecast({
            newPlanningRange,
          }),
        )

        if (!getUsedSpaceForecast.fulfilled.match(result)) {
          throw new Error(result.payload?.message ?? 'Internal error')
        }
        update({ status: 'ready' })
        return result.payload
      } catch (e) {
        toast.error(e.message)
      }
    },
    [dispatch, update, close, toast],
  )

  const onApplyChangesButtonClick = useCallback(async () => {
    update({ status: 'submitting' })
    const result = await dispatch(
      updatePlanningRange({
        planningRange: newPlanningRange,
        exportDeletedOrders: false,
      }),
    )

    if (!updatePlanningRange.fulfilled.match(result)) {
      throw new Error(result.payload?.message ?? 'Internal error')
    }

    update({ status: 'ready' })
    toast.success(texts.updated)
    close?.()
  }, [update, close, dispatch, newPlanningRange])

  const onDeleteButtonClick = useCallback(async () => {
    update({ status: 'submitting' })
    const result = await dispatch(
      updatePlanningRange({
        planningRange: newPlanningRange,
        exportDeletedOrders: false,
      }),
    )

    if (!updatePlanningRange.fulfilled.match(result)) {
      throw new Error(result.payload?.message ?? 'Internal error')
    }

    update({ status: 'ready' })
    toast.success(texts.updated)
    close?.()
  }, [update, close, dispatch, newPlanningRange])

  const onExportAndDeleteOrdersButtonClick = useCallback(async () => {
    update({ status: 'submitting' })
    const result = await dispatch(
      updatePlanningRange({
        planningRange: newPlanningRange,
        exportDeletedOrders: true,
        cfg: {
          format,
        },
      }),
    )

    if (updatePlanningRange.fulfilled.match(result)) {
      const url = unwrapResult(result)
      if (url) {
        window.open(url)
      }
    }
    if (!updatePlanningRange.fulfilled.match(result)) {
      throw new Error(result.payload?.message ?? 'Internal error')
    }

    update({ status: 'ready' })
    toast.success(texts.updated)
    close?.()
  }, [update, close, dispatch, newPlanningRange, format])

  const onChangeFormat = useCallback(
    (_e, value: string) => {
      if (!value) return
      updateData({ format: value as typeof format })
    },
    [updateData],
  )

  return {
    onPlanningRangeChange,
    onApplyChangesButtonClick,
    onDeleteButtonClick,
    onExportAndDeleteOrdersButtonClick,
    onChangeFormat,
  }
}
